<template>
  <v-container fluid class="pa-6">
    <!-- KPI Cards Section -->
    <v-row class="mb-4 mb-md-6">
      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="primary">mdi-cash-multiple</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Ventas Totales</div>
              <div class="kpi-value">{{ formatCurrency(totalSales) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <v-icon x-small color="success">mdi-trending-up</v-icon>
                {{ totalTransactions }} transacciones
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="success">mdi-package-variant</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Unidades</div>
              <div class="kpi-value">{{ formatNumber(totalQuantity) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <v-icon x-small color="info">mdi-account-group</v-icon>
                {{ activeVendors }} vendedores
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="warning">mdi-percent</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Descuentos</div>
              <div class="kpi-value">{{ formatCurrency(totalDiscounts) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                {{ discountPercentage }}% del total
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="info">mdi-trophy</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Top Vendedor</div>
              <div class="kpi-value text-truncate">{{ topVendor.nombre || "N/A" }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <v-icon x-small color="purple">mdi-star</v-icon>
                {{ formatCurrency(topVendor.valor || 0) }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content Card -->
    <v-card class="main-card elevation-0" outlined>
      <!-- Header with Actions -->
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h2 class="section-title" :class="{ 'text-h6': $vuetify.breakpoint.mobile }">
            Análisis de Ventas por Vendedor
          </h2>
          <v-spacer></v-spacer>

          <!-- Mobile menu -->
          <v-menu v-if="$vuetify.breakpoint.mobile" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="dateRangeMenu = true">
                <v-list-item-icon>
                  <v-icon>mdi-calendar-range</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ formatDateRange }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="get_report">
                <v-list-item-icon>
                  <v-icon>mdi-refresh</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Actualizar</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="exportToExcel">
                <v-list-item-icon>
                  <v-icon>mdi-file-excel</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Exportar</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- Desktop buttons -->
          <template v-else>
            <!-- Date Range Filters -->
            <v-menu
              v-model="dateRangeMenu"
              :close-on-content-click="false"
              offset-y
              min-width="300"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined v-bind="attrs" v-on="on" class="mr-2">
                  <v-icon left>mdi-calendar-range</v-icon>
                  {{ formatDateRange }}
                </v-btn>
              </template>
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="dateFrom"
                        label="Fecha Desde"
                        type="date"
                        outlined
                        dense
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="dateTo"
                        label="Fecha Hasta"
                        type="date"
                        outlined
                        dense
                        hide-details
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="dateRangeMenu = false">Cancelar</v-btn>
                  <v-btn color="primary" text @click="applyDateRange">Aplicar</v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>

            <v-btn color="primary" @click="get_report" :loading="loading_status">
              <v-icon left>mdi-refresh</v-icon>
              Actualizar
            </v-btn>

            <v-btn icon class="ml-2" @click="exportToExcel" title="Exportar a Excel">
              <v-icon>mdi-file-excel</v-icon>
            </v-btn>
          </template>
        </v-toolbar>
      </v-card-title>

      <!-- Data Table for Desktop / List for Mobile -->
      <template v-if="$vuetify.breakpoint.mobile">
        <!-- Mobile List View -->
        <v-list class="py-0">
          <template v-for="(item, index) in itemsWithSparkline">
            <v-list-item :key="item.codigo" class="px-4 py-3">
              <v-list-item-content>
                <div class="d-flex justify-space-between align-start mb-2">
                  <div>
                    <div class="font-weight-medium">{{ item.nombre }}</div>
                    <div class="text-caption grey--text">{{ item.codigo }}</div>
                  </div>
                  <v-chip
                    x-small
                    :color="getRatingColor(item.rating)"
                    text-color="black"
                    label
                  >
                    <v-icon x-small left>mdi-star</v-icon>
                    {{ item.rating.toFixed(1) }}
                  </v-chip>
                </div>

                <v-row no-gutters class="mb-2">
                  <v-col cols="6">
                    <div class="text-caption grey--text">Ventas</div>
                    <div class="font-weight-medium">{{ formatCurrency(item.valor) }}</div>
                    <div v-if="item.descuento > 0" class="text-caption error--text">
                      -{{ formatCurrency(item.descuento) }}
                    </div>
                  </v-col>
                  <v-col cols="6" class="text-right">
                    <div class="text-caption grey--text">Cantidad</div>
                    <div class="font-weight-medium">
                      {{ formatNumber(item.quantity) }}
                    </div>
                    <div class="text-caption">{{ item.percent }}%</div>
                  </v-col>
                </v-row>

                <div class="mt-2">
                  <v-sparkline
                    :value="item.sparklineData"
                    :gradient="['#1e88e5', '#1565c0']"
                    :smooth="16"
                    :padding="2"
                    :line-width="1"
                    :stroke-linecap="'round'"
                    :gradient-direction="'top'"
                    :fill="true"
                    :type="'trend'"
                    :auto-draw="true"
                    height="30"
                  />
                </div>
              </v-list-item-content>
            </v-list-item>
            <v-divider
              v-if="index < itemsWithSparkline.length - 1"
              :key="`div-${item.codigo}`"
            />
          </template>

          <!-- Mobile Totals -->
          <v-list-item class="grey lighten-4 py-3">
            <v-list-item-content>
              <div class="font-weight-bold text-center mb-2">TOTALES</div>
              <v-row no-gutters>
                <v-col cols="6">
                  <div class="text-caption">Ventas</div>
                  <div class="font-weight-bold">{{ formatCurrency(totalSales) }}</div>
                  <div class="text-caption error--text">
                    -{{ formatCurrency(totalDiscounts) }}
                  </div>
                </v-col>
                <v-col cols="6" class="text-right">
                  <div class="text-caption">Cantidad</div>
                  <div class="font-weight-bold">{{ formatNumber(totalQuantity) }}</div>
                  <div class="text-caption">100%</div>
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>

      <template v-else>
        <!-- Desktop Table View -->
        <v-data-table
          :headers="headers"
          :items="itemsWithSparkline"
          :items-per-page="10"
          item-key="codigo"
          sort-by="valor"
          sort-desc
          :loading="loading_status"
          loader-height="3"
          loading-text="Cargando datos..."
          class="minimal-table"
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100],
          }"
        >
          <!-- Vendor Info -->
          <template v-slot:[`item.vendor`]="{ item }">
            <div class="vendor-info">
              <div class="vendor-name">
                <v-icon small class="mr-2" color="primary">mdi-account-tie</v-icon>
                {{ item.nombre }}
              </div>
              <div class="vendor-code text-caption grey--text">
                Código: {{ item.codigo }}
              </div>
            </div>
          </template>

          <!-- Sales Info -->
          <template v-slot:[`item.sales`]="{ item }">
            <div class="sales-info">
              <div class="sales-value font-weight-medium">
                {{ formatCurrency(item.valor) }}
              </div>
              <div
                v-if="item.descuento > 0"
                class="sales-discount text-caption error--text"
              >
                -{{ formatCurrency(item.descuento) }} desc.
              </div>
            </div>
          </template>

          <!-- Quantity with Progress -->
          <template v-slot:[`item.quantity`]="{ item }">
            <div class="quantity-info">
              <div class="d-flex align-center">
                <span class="font-weight-medium mr-2">{{
                  formatNumber(item.quantity)
                }}</span>
                <v-progress-linear
                  :value="(item.quantity / maxQuantity) * 100"
                  height="6"
                  color="success"
                  rounded
                  style="max-width: 100px"
                />
              </div>
              <div class="text-caption grey--text">{{ item.percent }}% del total</div>
            </div>
          </template>

          <!-- Performance Metrics -->
          <template v-slot:[`item.performance`]="{ item }">
            <div class="performance-info text-center">
              <v-chip small :color="getRatingColor(item.rating)" text-color="black" label>
                <v-icon x-small left>mdi-star</v-icon>
                {{ item.rating.toFixed(1) }}
              </v-chip>
              <div class="text-caption grey--text mt-1">
                {{ item.promedio.toFixed(1) }} pares/día
              </div>
            </div>
          </template>

          <!-- Sparkline Trend -->
          <template v-slot:[`item.trend`]="{ item }">
            <div class="sparkline-cell">
              <v-sparkline
                :value="item.sparklineData"
                :gradient="['#1e88e5', '#1565c0']"
                :smooth="16"
                :padding="4"
                :line-width="1.5"
                :stroke-linecap="'round'"
                :gradient-direction="'top'"
                :fill="true"
                :type="'trend'"
                :auto-draw="true"
                height="35"
                width="100"
              />
            </div>
          </template>

          <!-- Footer with totals -->
          <template v-slot:body.append>
            <tr class="grey lighten-4">
              <td class="text-right font-weight-bold">TOTALES</td>
              <td class="text-right font-weight-bold">
                <div>{{ formatCurrency(totalSales) }}</div>
                <div class="text-caption error--text">
                  -{{ formatCurrency(totalDiscounts) }}
                </div>
              </td>
              <td class="text-center font-weight-bold">
                <div>{{ formatNumber(totalQuantity) }}</div>
                <div class="text-caption">100%</div>
              </td>
              <td class="text-center">
                <v-chip small color="primary" label>
                  <v-icon x-small left>mdi-sigma</v-icon>
                  {{ averageRating.toFixed(1) }}
                </v-chip>
              </td>
              <td></td>
            </tr>
          </template>
        </v-data-table>
      </template>
    </v-card>

    <!-- Performance Chart Card -->
    <v-card class="mt-6 elevation-0" outlined v-if="items.length > 0">
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h3 class="subtitle-1">Distribución de Ventas</h3>
        </v-toolbar>
      </v-card-title>
      <v-card-text class="pa-4">
        <!-- Mobile View -->
        <template v-if="$vuetify.breakpoint.mobile">
          <v-sparkline
            :value="chartData"
            :gradient="gradient"
            :smooth="10"
            :padding="8"
            :line-width="2"
            :stroke-linecap="'round'"
            :gradient-direction="'top'"
            :fill="true"
            :type="'bar'"
            :auto-draw="true"
            height="120"
            :show-labels="false"
            class="mb-4"
          />

          <v-list dense class="transparent">
            <v-list-item v-for="(vendor, index) in topVendors" :key="index" class="px-0">
              <v-list-item-avatar size="32">
                <v-chip :color="getPositionColor(index)" small>
                  {{ index + 1 }}
                </v-chip>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-caption">
                  {{ vendor.nombre }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <div class="text-right">
                  <div class="font-weight-medium text-caption">
                    {{ formatCurrency(vendor.valor) }}
                  </div>
                  <div class="text-caption grey--text">{{ vendor.percent }}%</div>
                </div>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </template>

        <!-- Desktop View -->
        <template v-else>
          <v-row>
            <v-col cols="12" lg="8">
              <div class="sparkline-container">
                <v-sparkline
                  :value="chartData"
                  :gradient="gradient"
                  :smooth="10"
                  :padding="8"
                  :line-width="2"
                  :stroke-linecap="'round'"
                  :gradient-direction="'top'"
                  :fill="true"
                  :type="'bar'"
                  :auto-draw="true"
                  height="200"
                  :show-labels="false"
                  :label-size="2"
                />
              </div>
            </v-col>
            <v-col cols="12" lg="4">
              <div class="top-vendors-table">
                <h4 class="text-subtitle-2 mb-3">Top 5 Vendedores</h4>
                <v-simple-table dense>
                  <tbody>
                    <tr v-for="(vendor, index) in topVendors" :key="index">
                      <td width="10%">
                        <v-chip x-small :color="getPositionColor(index)">
                          {{ index + 1 }}
                        </v-chip>
                      </td>
                      <td class="text-truncate" style="max-width: 150px">
                        {{ vendor.nombre }}
                      </td>
                      <td class="text-right">{{ formatCurrency(vendor.valor) }}</td>
                      <td class="text-right">
                        <span class="text-caption">{{ vendor.percent }}%</span>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-col>
          </v-row>
        </template>
      </v-card-text>
    </v-card>

    <!-- Snackbar para mensajes -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, getToday, getdays_ago } from "@/services/webserver.js";

export default {
  name: "VendorsStat",
  data() {
    return {
      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },

      // UI State
      loading_status: false,
      dateRangeMenu: false,

      // Headers modernizados
      headers: [
        {
          text: "Vendedor",
          align: "start",
          sortable: true,
          value: "vendor",
          width: "25%",
        },
        {
          text: "Ventas",
          align: "end",
          sortable: true,
          value: "sales",
          width: "20%",
        },
        {
          text: "Cantidad",
          align: "center",
          sortable: true,
          value: "quantity",
          width: "20%",
        },
        {
          text: "Rendimiento",
          align: "center",
          sortable: true,
          value: "performance",
          width: "15%",
        },
        {
          text: "Tendencia",
          align: "center",
          sortable: false,
          value: "trend",
          width: "20%",
        },
      ],

      // Data
      items: [],
      dateFrom: getdays_ago(-30),
      dateTo: getToday(),

      // Sparkline settings
      gradient: ["#1e88e5", "#1976d2", "#1565c0"],
    };
  },

  computed: {
    formatDateRange() {
      const from = new Date(this.dateFrom).toLocaleDateString("es");
      const to = new Date(this.dateTo).toLocaleDateString("es");
      return `${from} - ${to}`;
    },

    totalSales() {
      return this.items.reduce((sum, item) => sum + parseFloat(item.valor || 0), 0);
    },

    totalQuantity() {
      return this.items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
    },

    totalDiscounts() {
      return this.items.reduce((sum, item) => sum + parseFloat(item.descuento || 0), 0);
    },

    totalTransactions() {
      return this.items.length;
    },

    activeVendors() {
      return new Set(this.items.map((item) => item.codigo)).size;
    },

    discountPercentage() {
      if (this.totalSales === 0) return 0;
      return Math.round(
        (this.totalDiscounts / (this.totalSales + this.totalDiscounts)) * 100
      );
    },

    topVendor() {
      if (this.items.length === 0) return {};
      return this.items.reduce(
        (max, item) => (parseFloat(item.valor) > parseFloat(max.valor || 0) ? item : max),
        {}
      );
    },

    maxQuantity() {
      return Math.max(...this.items.map((item) => parseFloat(item.quantity || 0)));
    },

    averageRating() {
      if (this.items.length === 0) return 0;
      const sum = this.items.reduce((acc, item) => acc + parseFloat(item.rating || 0), 0);
      return sum / this.items.length;
    },

    itemsWithSparkline() {
      return this.items.map((item) => ({
        ...item,
        sparklineData: this.generateSparklineData(item),
      }));
    },

    chartData() {
      // Crear una copia para evitar mutación
      const sortedItems = [...this.items].sort((a, b) => b.valor - a.valor);
      return sortedItems.slice(0, 10).map((item) => item.valor);
    },

    topVendors() {
      // Crear una copia para evitar mutación
      const sortedItems = [...this.items].sort((a, b) => b.valor - a.valor);
      return sortedItems.slice(0, 5);
    },
  },

  mounted() {
    this.get_report();
  },

  methods: {
    // Mostrar mensajes
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    // Formatear moneda
    formatCurrency(value) {
      if (!value && value !== 0) return "$ 0";
      return (
        "$ " +
        parseFloat(value).toLocaleString("es", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      );
    },

    // Formatear números
    formatNumber(value) {
      if (!value && value !== 0) return "0";
      return parseFloat(value).toLocaleString("es", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },

    // Generar datos para sparkline
    generateSparklineData(item) {
      // Simulamos una tendencia basada en los datos actuales
      // En producción, estos datos vendrían del backend
      const baseValue = item.valor / 30;
      const data = [];
      for (let i = 0; i < 10; i++) {
        data.push(baseValue * (0.5 + Math.random()));
      }
      return data;
    },

    // Obtener color según rating
    getRatingColor(rating) {
      const ratingValue = rating || 0;
      if (ratingValue >= 4) return "green lighten-4";
      if (ratingValue >= 3) return "amber lighten-4";
      if (ratingValue >= 2) return "orange lighten-4";
      return "red lighten-4";
    },

    getRatingTextColor() {
      return "black";
    },

    // Obtener color de posición
    getPositionColor(index) {
      const colors = ["gold", "silver", "bronze", "grey lighten-2", "grey lighten-3"];
      return colors[index] || "grey lighten-3";
    },

    // Aplicar rango de fechas
    applyDateRange() {
      this.dateRangeMenu = false;
      this.get_report();
    },

    // Obtener reporte
    get_report() {
      this.loading_status = true;

      const qry = {
        store: window.store?.store_id || window.profile?.store_id,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
      };

      webserver(
        "vendors_stat",
        qry,
        (data) => {
          this.loading_status = false;

          // Procesar datos
          this.items = data.map((item) => ({
            ...item,
            valor: parseFloat(item.valor || 0),
            descuento: parseFloat(item.descuento || 0),
            quantity: parseFloat(item.quantity || 0),
            days: parseInt(item.days || 1),
            promedio: parseFloat(item.quantity || 0) / parseInt(item.days || 1),
            rating: this.calculateRating(item),
            percent: this.calculatePercentage(item, data),
          }));

          this.showMessage(
            `Reporte generado: ${this.items.length} vendedores`,
            "success"
          );
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al cargar el reporte: " + error, "error");
        }
      );
    },

    // Calcular rating del vendedor
    calculateRating(item) {
      // Fórmula simple basada en ventas y cantidad
      const salesScore = (item.valor / 1000000) * 2; // Por cada millón, 2 puntos
      const quantityScore = (item.quantity / 100) * 1; // Por cada 100 unidades, 1 punto
      const score = Math.min(5, salesScore + quantityScore);
      return Math.max(1, score);
    },

    // Calcular porcentaje
    calculatePercentage(item, allItems) {
      const total = allItems.reduce((sum, i) => sum + parseFloat(i.quantity || 0), 0);
      if (total === 0) return 0;
      return Math.round((item.quantity / total) * 100);
    },

    // Exportar a Excel
    exportToExcel() {
      const csv = this.generateCSV();
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `ventas_vendedores_${this.dateFrom}_${this.dateTo}.csv`;
      link.click();
      this.showMessage("Archivo exportado exitosamente", "success");
    },

    // Generar CSV
    generateCSV() {
      const headers = [
        "Código",
        "Nombre",
        "Ventas",
        "Descuentos",
        "Cantidad",
        "Días",
        "Promedio/Día",
        "Rating",
        "Porcentaje",
      ];

      const rows = this.items.map((item) => [
        item.codigo || "",
        item.nombre || "",
        item.valor || 0,
        item.descuento || 0,
        item.quantity || 0,
        item.days || 0,
        item.promedio.toFixed(2),
        item.rating.toFixed(1),
        item.percent + "%",
      ]);

      const totals = [
        "TOTAL",
        "",
        this.totalSales,
        this.totalDiscounts,
        this.totalQuantity,
        "",
        "",
        this.averageRating.toFixed(1),
        "100%",
      ];

      rows.push(totals);

      const csv = [headers, ...rows]
        .map((row) =>
          row.map((cell) => `"${cell.toString().replace(/"/g, '""')}"`).join(",")
        )
        .join("\n");

      return csv;
    },
  },
};
</script>

<style scoped>
/* KPI Cards */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 28px !important;
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

/* Main Card */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

/* Data Table */
.minimal-table {
  border: none !important;
}

.minimal-table >>> .v-data-table__wrapper {
  border: none;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
  padding: 16px !important;
}

.minimal-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
  transition: background-color 0.2s;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.minimal-table >>> tbody tr td {
  padding: 16px !important;
  font-size: 0.875rem !important;
}

/* Custom cells */
.vendor-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.vendor-name {
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
}

.sales-info {
  text-align: right;
}

.quantity-info {
  min-width: 150px;
}

.performance-info {
  min-width: 100px;
}

/* Loading */
.minimal-table >>> .v-data-table__progress {
  height: 2px !important;
}

/* Sparkline container */
.sparkline-container {
  overflow-x: auto;
  overflow-y: hidden;
}

.sparkline-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Top vendors table */
.top-vendors-table {
  overflow-x: auto;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .kpi-value {
    font-size: 1.25rem;
  }

  .section-title {
    font-size: 1.125rem;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
    padding: 16px !important;
  }

  .kpi-icon {
    margin-bottom: 8px;
  }

  .minimal-toolbar {
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px !important;
  }

  .minimal-table >>> thead tr th {
    padding: 12px 8px !important;
    font-size: 0.75rem !important;
  }

  .minimal-table >>> tbody tr td {
    padding: 12px 8px !important;
    font-size: 0.75rem !important;
  }

  .vendor-name {
    font-size: 0.875rem;
  }

  .sales-value {
    font-size: 0.875rem;
  }

  /* Hide some columns on mobile */
  .minimal-table >>> thead tr th:nth-child(4),
  .minimal-table >>> tbody tr td:nth-child(4) {
    display: none;
  }
}

@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .kpi-card .v-card__text {
    flex-direction: row;
    text-align: left;
  }
}
</style>
